import React from 'react';
import { Badge, Button } from '../kit';
import { isDeactivated, longDateTime } from './userModel';
import SectionHead from './SectionHead';

const COGNITO_STATUS = {
  FORCE_CHANGE_PASSWORD: { tone: 'warn', label: "Invited — hasn't set a password yet" },
  CONFIRMED: { tone: 'ok', label: 'Password set' },
  RESET_REQUIRED: { tone: 'warn', label: 'Password reset required' },
  UNCONFIRMED: { tone: 'warn', label: 'Email not confirmed' },
  EXTERNAL_PROVIDER: { tone: undefined, label: 'Signs in with an external provider' },
  ARCHIVED: { tone: 'bad', label: 'Archived' },
  COMPROMISED: { tone: 'bad', label: 'Flagged as compromised' },
};

const MFA_METHOD = {
  SMS_MFA: 'text message',
  SOFTWARE_TOKEN_MFA: 'authenticator app',
  EMAIL_OTP: 'email code',
};

export const maskPhone = (phone) => (phone ? String(phone).replace(/^(\+\d{1,3})\d+(\d{2})$/, '$1 •••• ••$2') : null);

function Row({ title, detail, children }) {
  return (
    <div className="us-row">
      <div className="us-row-text">
        <span className="us-row-title">{title}</span>
        {detail ? <span className="ak-help">{detail}</span> : null}
      </div>
      {children ? <div className="us-row-actions">{children}</div> : null}
    </div>
  );
}

/**
 * Security section (edit only): MFA, password, sign-in account.
 * security: { loading, data: { cognito, error } | null, error }
 */
export default function SecuritySection({ user, security, onResetPassword, onResetMfa, onDeactivate, onRestore, onResendInvite, canResendInvite, isViewer }) {
  const cognito = security.data?.cognito;
  const unavailable = security.error || security.data?.error;
  const deactivated = isDeactivated(user);
  const status = cognito?.status ? COGNITO_STATUS[cognito.status] || { label: cognito.status } : null;

  const mfaDetail = (() => {
    if (security.loading) return 'Checking…';
    if (unavailable) return `Sign-in details unavailable: ${String(unavailable)}`;
    if (!cognito || cognito.exists === false) return 'No sign-in account';
    const parts = [cognito.mfaEnabled ? 'On' : 'Off'];
    if (cognito.mfaMethods?.length) parts.push(cognito.mfaMethods.map((m) => MFA_METHOD[m] || m).join(', '));
    if (cognito.phoneNumber) parts.push(`${maskPhone(cognito.phoneNumber)}${cognito.phoneVerified ? '' : ' (not verified)'}`);
    if (typeof cognito.rememberedDevices === 'number' && cognito.rememberedDevices > 0) {
      parts.push(`${cognito.rememberedDevices} remembered device${cognito.rememberedDevices === 1 ? '' : 's'}`);
    }
    if (user?.mfaResetAt) parts.push(`last reset ${longDateTime(user.mfaResetAt)}${user.mfaResetBy ? ` by ${user.mfaResetBy}` : ''}`);
    return parts.join(' · ');
  })();

  const accountDetail = (() => {
    if (deactivated) return "Deactivated — they can't sign in. Their access is kept.";
    if (security.loading) return 'Checking…';
    const bits = [];
    if (cognito?.exists === false) bits.push('No sign-in account');
    else if (status) bits.push(status.label);
    if (cognito && cognito.exists && !cognito.enabled) bits.push('disabled in sign-in');
    else if (cognito?.exists) bits.push('enabled');
    if (cognito?.createdAt) bits.push(`created ${longDateTime(cognito.createdAt)}`);
    return bits.join(' · ') || '—';
  })();

  return (
    <section className="us-section" aria-labelledby="us-sec-security">
      <SectionHead id="us-sec-security" title="Security" />
      <div className="us-card us-rows">
        <Row title="Multi-factor authentication" detail={mfaDetail}>
          <Button size="small" onClick={onResetMfa} disabled={deactivated}>Reset MFA</Button>
        </Row>
        <Row title="Password" detail="Send a new temporary password. They'll choose a new one at sign-in.">
          <Button size="small" onClick={onResetPassword} disabled={deactivated}>Reset password</Button>
        </Row>
        <Row
          title={(
            <>
              Sign-in account{' '}
              {status && !deactivated ? <Badge tone={status.tone}>{status.tone === 'ok' ? 'Confirmed' : 'Needs attention'}</Badge> : null}
            </>
          )}
          detail={accountDetail}
        >
          {canResendInvite ? <Button size="small" onClick={onResendInvite}>Resend invite</Button> : null}
          {isViewer ? null : deactivated
            ? <Button size="small" onClick={onRestore}>Restore access</Button>
            : <Button size="small" variant="danger" onClick={onDeactivate}>Deactivate</Button>}
        </Row>
      </div>
    </section>
  );
}
